import { Controller } from '@hotwired/stimulus'
import Cropper from 'cropperjs'

const OUTPUT_SIZE = 600
const HEIC_TYPES = ['image/heic', 'image/heif']
const HEIC_EXTENSION = /\.(heic|heif)$/i

// After a resize the crop box grows back to this share of the container,
// zooming the photo along with it
const CROP_BOX_FILL = 0.8
const RESIZE_ACTIONS = ['crop', 'n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw']
const MAX_IMAGE_RATIO = 2
const ZOOM_DURATION = 300

export default class extends Controller {
  static targets = ['input', 'output', 'dialog', 'image', 'loader', 'error']

  connect () {
    this.committedFiles = new window.DataTransfer().files
  }

  disconnect () {
    this.#destroyCropper()
    this.#revokeSourceUrl()
  }

  async select () {
    const file = this.inputTarget.files[0]
    if (!file) return

    this.saved = false
    this.errorTarget.classList.add('hidden')
    this.imageTarget.classList.add('invisible')
    this.loaderTarget.classList.remove('hidden')
    this.dialogTarget.showModal()

    try {
      const source = await this.#toBrowserImage(file)
      if (!this.dialogTarget.open) return

      this.#revokeSourceUrl()
      this.sourceUrl = URL.createObjectURL(source)
      this.imageTarget.src = this.sourceUrl
    } catch (e) {
      console.error(e)
      this.loaderTarget.classList.add('hidden')
      this.errorTarget.classList.remove('hidden')
    }
  }

  imageLoaded () {
    this.#destroyCropper()
    this.loaderTarget.classList.add('hidden')
    this.imageTarget.classList.remove('invisible')

    this.cropper = new Cropper(this.imageTarget, {
      aspectRatio: 1,
      viewMode: 1,
      dragMode: 'move',
      autoCropArea: 0.8,
      background: false,
      guides: false,
      center: false,
      toggleDragModeOnDblclick: false,
      cropend: (event) => {
        if (RESIZE_ACTIONS.includes(event.detail.action)) this.#zoomToCropBox()
      }
    })
  }

  imageFailed () {
    if (!this.imageTarget.getAttribute('src')) return

    this.loaderTarget.classList.add('hidden')
    this.errorTarget.classList.remove('hidden')
  }

  rotateLeft () {
    this.cropper?.rotate(-90)
  }

  rotateRight () {
    this.cropper?.rotate(90)
  }

  save () {
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas({
      width: OUTPUT_SIZE,
      height: OUTPUT_SIZE,
      fillColor: '#fff',
      imageSmoothingQuality: 'high'
    })

    canvas.toBlob((blob) => {
      const file = new window.File([blob], 'avatar.jpg', { type: 'image/jpeg' })
      const transfer = new window.DataTransfer()
      transfer.items.add(file)

      this.inputTarget.files = transfer.files
      this.committedFiles = transfer.files
      this.outputTarget.src = canvas.toDataURL('image/jpeg')
      this.saved = true
      this.dialogTarget.close()
    }, 'image/jpeg', 0.9)
  }

  cancel () {
    this.dialogTarget.close()
  }

  // Fires on Save, Cancel and Esc alike
  closed () {
    if (!this.saved) this.inputTarget.files = this.committedFiles

    this.#destroyCropper()
    this.#revokeSourceUrl()
    this.imageTarget.removeAttribute('src')
  }

  #zoomToCropBox () {
    const container = this.cropper.getContainerData()
    const canvas = this.cropper.getCanvasData()
    const box = this.cropper.getCropBoxData()

    const targetSize = Math.min(container.width, container.height) * CROP_BOX_FILL
    const maxScale = (canvas.naturalWidth * MAX_IMAGE_RATIO) / canvas.width
    const scale = Math.min(targetSize / box.width, maxScale)
    if (Math.abs(scale - 1) < 0.05) return

    const size = box.width * scale
    const toBox = {
      left: (container.width - size) / 2,
      top: (container.height - size) / 2,
      width: size,
      height: size
    }
    const toCanvas = {
      left: toBox.left - (box.left - canvas.left) * scale,
      top: toBox.top - (box.top - canvas.top) * scale,
      width: canvas.width * scale
    }

    this.#animate(canvas, toCanvas, box, toBox)
  }

  #animate (fromCanvas, toCanvas, fromBox, toBox) {
    window.cancelAnimationFrame(this.zoomFrame)
    const start = window.performance.now()
    const lerp = (from, to, t) => Object.fromEntries(
      Object.keys(to).map((key) => [key, from[key] + (to[key] - from[key]) * t])
    )

    const step = (now) => {
      if (!this.cropper) return

      const progress = Math.min((now - start) / ZOOM_DURATION, 1)
      const t = 1 - Math.pow(1 - progress, 3)

      // Canvas first: in viewMode 1 the crop box is clamped to the canvas
      this.cropper.setCanvasData(lerp(fromCanvas, toCanvas, t))
      this.cropper.setCropBoxData(lerp(fromBox, toBox, t))

      if (progress < 1) this.zoomFrame = window.requestAnimationFrame(step)
    }

    this.zoomFrame = window.requestAnimationFrame(step)
  }

  async #toBrowserImage (file) {
    if (!this.#isHeic(file)) return file

    const { heicTo } = await import('heic-to')
    return heicTo({ blob: file, type: 'image/jpeg', quality: 0.92 })
  }

  #isHeic (file) {
    return HEIC_TYPES.includes(file.type) || HEIC_EXTENSION.test(file.name)
  }

  #destroyCropper () {
    window.cancelAnimationFrame(this.zoomFrame)
    this.cropper?.destroy()
    this.cropper = null
  }

  #revokeSourceUrl () {
    if (this.sourceUrl) URL.revokeObjectURL(this.sourceUrl)
    this.sourceUrl = null
  }
}
